/**
 * Lean Setup Wizard — Page 1: Shop basics.
 *
 * Fields: shop name, time zone, weekly hours.
 * Manual path renders blank; Import path can prefill via props.
 */

import { useState } from 'react';
import { Search, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import type { Page1Data } from '@/lib/onboarding/wizardValidation';
import { page1Schema } from '@/lib/onboarding/wizardValidation';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { DEFAULT_SHOP_HOURS_MON_FRI_8_5 } from '@/lib/shopHours/defaults';

const WEEKDAYS: Array<{ key: string; label: string }> = [
  { key: 'monday', label: 'Monday' },
  { key: 'tuesday', label: 'Tuesday' },
  { key: 'wednesday', label: 'Wednesday' },
  { key: 'thursday', label: 'Thursday' },
  { key: 'friday', label: 'Friday' },
  { key: 'saturday', label: 'Saturday' },
  { key: 'sunday', label: 'Sunday' },
];

const TIMEZONES = [
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
];

interface ShopBasicsStepProps {
  initial: Partial<Page1Data>;
  onBack?: () => void;
  onNext: (data: Page1Data) => void;
}

export function ShopBasicsStep({ initial, onBack, onNext }: ShopBasicsStepProps) {
  const { activeShop } = useAuth();
  const [name, setName] = useState(initial.name ?? '');
  const [timeZone, setTimeZone] = useState(initial.time_zone ?? 'America/Los_Angeles');
  const [streetAddress, setStreetAddress] = useState(initial.street_address ?? '');
  const [city, setCity] = useState(initial.city ?? '');
  const [stateRegion, setStateRegion] = useState(initial.state ?? '');
  const [postalCode, setPostalCode] = useState(initial.postal_code ?? '');
  const [country, setCountry] = useState(initial.country ?? 'US');
  // Batch 8B: initialize from `initial.hours` when present (e.g.,
  // import path passes CSV-derived prefill), otherwise from the
  // canonical Mon-Fri 8-5 default. The "Look up shop info" button
  // replaces these once a provider returns hours.
  const [hours, setHours] = useState<Record<string, { open: string; close: string } | undefined>>(
    (initial.hours as any) ?? { ...DEFAULT_SHOP_HOURS_MON_FRI_8_5 },
  );
  // Batch 8B: when the wizard receives provider-returned hours, we
  // collapse the manual editor by default — the operator can expand
  // to review/edit. For the manual flow, leave the editor expanded
  // so existing UX doesn't change.
  const [hoursEditorOpen, setHoursEditorOpen] = useState<boolean>(true);
  // Tracks whether the current `hours` state was set by the public
  // lookup. Drives the source label on the editor section.
  const [hoursSource, setHoursSource] = useState<'manual' | 'public_listing' | 'grounded_search' | 'default'>(
    'manual',
  );
  const [errors, setErrors] = useState<string[]>([]);
  const [isLookingUp, setIsLookingUp] = useState(false);
  // Source label for the lookup banner. Drives the "Found from public
  // listing" / "Address normalized but no public hours provider
  // configured" copy below the address fields.
  const [lookupStatus, setLookupStatus] = useState<
    | { kind: 'idle' }
    | { kind: 'found_address'; provider: string; hoursStatus: string }
    | { kind: 'no_match' }
    | { kind: 'no_provider' }
    | { kind: 'error'; message: string }
  >({ kind: 'idle' });

  const updateDay = (day: string, field: 'open' | 'close', value: string) => {
    setHours((prev) => ({
      ...prev,
      [day]: {
        open: field === 'open' ? value : (prev[day]?.open ?? ''),
        close: field === 'close' ? value : (prev[day]?.close ?? ''),
      },
    }));
  };

  const toggleDay = (day: string, on: boolean) => {
    setHours((prev) => ({
      ...prev,
      [day]: on ? { open: '08:00', close: '17:00' } : undefined,
    }));
  };

  const handleNext = () => {
    const cleaned: Record<string, { open: string; close: string }> = {};
    for (const [k, v] of Object.entries(hours)) {
      if (v?.open && v?.close) cleaned[k] = v;
    }
    const data = {
      name: name.trim(),
      time_zone: timeZone,
      hours: cleaned,
      street_address: streetAddress.trim(),
      city: city.trim(),
      state: stateRegion.trim(),
      postal_code: postalCode.trim(),
      country: country.trim() || 'US',
    };
    const parsed = page1Schema.safeParse(data);
    if (!parsed.success) {
      setErrors(parsed.error.issues.map((i) => i.message));
      return;
    }
    setErrors([]);
    onNext(parsed.data);
  };

  /**
   * Calls the `lookup-shop-public-info` Edge Function and prefills
   * normalized address / lat-lng from the provider response. The
   * function is server-side only — provider API keys never reach
   * the browser. If no provider key is configured we still get a
   * `no_provider_configured` response and surface that source label
   * to the operator.
   */
  const handleLookup = async () => {
    if (!activeShop?.id || !name.trim()) return;
    setIsLookingUp(true);
    setLookupStatus({ kind: 'idle' });
    try {
      const { data, error } = await supabase.functions.invoke('lookup-shop-public-info', {
        body: {
          shopId: activeShop.id,
          shopName: name.trim(),
          streetAddress: streetAddress.trim() || undefined,
          city: city.trim() || undefined,
          state: stateRegion.trim() || undefined,
          postalCode: postalCode.trim() || undefined,
          country: country.trim() || undefined,
        },
      });
      if (error) {
        setLookupStatus({ kind: 'error', message: error.message ?? 'Lookup failed' });
        return;
      }
      const body = (data ?? {}) as {
        ok?: boolean;
        geocode?: {
          status: string;
          provider: string;
          address?: {
            streetAddress?: string;
            city?: string;
            state?: string;
            postalCode?: string;
            country?: string;
          };
        };
        hours?: {
          status: string;
          provider?: string;
          // Batch 8B: 7-element by-weekday array, Sunday=0..Saturday=6.
          byWeekday?: Array<{ open: string; close: string } | null | undefined>;
        };
      };
      if (body.geocode?.status === 'public_listing_found') {
        const addr = body.geocode.address ?? {};
        if (!streetAddress && addr.streetAddress) setStreetAddress(addr.streetAddress);
        if (!city && addr.city) setCity(addr.city);
        if (!stateRegion && addr.state) setStateRegion(addr.state);
        if (!postalCode && addr.postalCode) setPostalCode(addr.postalCode);
        if (!country && addr.country) setCountry(addr.country);
      }
      // Batch 8B: if the hours provider returned actual hours, project
      // them into the local hours state and tag the source. This is the
      // happy path where the operator never has to type hours.
      if (body.hours?.status === 'public_listing_found' && Array.isArray(body.hours.byWeekday)) {
        const next: Record<string, { open: string; close: string } | undefined> = {};
        const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
        body.hours.byWeekday.forEach((day, idx) => {
          if (day && typeof day.open === 'string' && typeof day.close === 'string') {
            next[dayNames[idx]] = { open: day.open, close: day.close };
          }
        });
        if (Object.keys(next).length > 0) {
          setHours(next);
          setHoursSource(body.hours.provider === 'gemini_grounded' ? 'grounded_search' : 'public_listing');
          // Collapse the editor by default once provider hours land —
          // the operator can still expand to review/edit.
          setHoursEditorOpen(false);
        }
      }
      if (body.geocode?.status === 'public_listing_found') {
        setLookupStatus({
          kind: 'found_address',
          provider: body.geocode.provider,
          hoursStatus: body.hours?.status ?? 'no_provider_configured',
        });
      } else if (body.geocode?.status === 'no_provider_configured') {
        setLookupStatus({ kind: 'no_provider' });
      } else {
        setLookupStatus({ kind: 'no_match' });
      }
    } catch (e) {
      setLookupStatus({
        kind: 'error',
        message: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setIsLookingUp(false);
    }
  };

  return (
    <Card className="w-full max-w-2xl" data-testid="wizard-step-shop-basics">
      <CardHeader>
        <CardTitle>Shop Basics</CardTitle>
        <CardDescription>Start with the essentials — you can refine later in Settings.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="wizard-shop-name">Shop name</Label>
          <Input
            id="wizard-shop-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Premier Auto Service"
            data-testid="wizard-shop-name"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="wizard-timezone">Time zone</Label>
          <select
            id="wizard-timezone"
            value={timeZone}
            onChange={(e) => setTimeZone(e.target.value)}
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            data-testid="wizard-timezone"
          >
            {TIMEZONES.map((tz) => (
              <option key={tz.value} value={tz.value}>{tz.label}</option>
            ))}
          </select>
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label>Shop address (optional)</Label>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleLookup}
              disabled={isLookingUp || !name.trim() || !activeShop?.id}
              data-testid="wizard-lookup-shop-info"
            >
              {isLookingUp ? (
                <Loader2 className="mr-1 h-3 w-3 animate-spin" />
              ) : (
                <Search className="mr-1 h-3 w-3" />
              )}
              {isLookingUp ? 'Looking up…' : 'Look up shop info'}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Address helps OptiBay match your shop to its public business listing
            and prefill hours. You can fill these in later — they're optional.
          </p>
          <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
            <Input
              placeholder="Street address"
              value={streetAddress}
              onChange={(e) => setStreetAddress(e.target.value)}
              data-testid="wizard-shop-street-address"
            />
            <Input
              placeholder="City"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              data-testid="wizard-shop-city"
            />
            <Input
              placeholder="State / Region"
              value={stateRegion}
              onChange={(e) => setStateRegion(e.target.value)}
              data-testid="wizard-shop-state"
            />
            <Input
              placeholder="ZIP / Postal code"
              value={postalCode}
              onChange={(e) => setPostalCode(e.target.value)}
              data-testid="wizard-shop-postal-code"
            />
            <Input
              placeholder="Country (e.g. US)"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              maxLength={2}
              data-testid="wizard-shop-country"
            />
          </div>
          {lookupStatus.kind === 'found_address' && (
            <p
              className="rounded border border-emerald-500/40 bg-emerald-500/5 p-2 text-xs text-emerald-700"
              data-testid="wizard-lookup-found-banner"
            >
              Found from public business listing ({lookupStatus.provider}).{' '}
              {lookupStatus.hoursStatus === 'public_listing_found'
                ? 'Hours prefilled from public listing.'
                : 'Public hours provider is not configured yet — using your manual / CSV hours.'}
            </p>
          )}
          {lookupStatus.kind === 'no_match' && (
            <p
              className="rounded border border-amber-500/40 bg-amber-500/5 p-2 text-xs text-amber-700"
              data-testid="wizard-lookup-nomatch-banner"
            >
              Couldn't find a public listing for that name + address. You can keep
              going with manual hours.
            </p>
          )}
          {lookupStatus.kind === 'no_provider' && (
            <p
              className="rounded border border-muted-foreground/40 bg-muted/40 p-2 text-xs text-muted-foreground"
              data-testid="wizard-lookup-noprovider-banner"
            >
              No location provider is configured on this OptiBay deployment.
              Address fields will save but won't auto-prefill from a public
              listing.
            </p>
          )}
          {lookupStatus.kind === 'error' && (
            <p
              className="rounded border border-destructive/40 bg-destructive/5 p-2 text-xs text-destructive"
              data-testid="wizard-lookup-error-banner"
            >
              Lookup failed: {lookupStatus.message}
            </p>
          )}
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label>Shop hours</Label>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setHoursEditorOpen((prev) => !prev)}
              data-testid="wizard-hours-toggle-editor"
            >
              {hoursEditorOpen ? 'Hide' : 'Review or edit hours'}
            </Button>
          </div>
          {/* Batch 8B: source label so the operator knows whether
              hours came from a public listing, grounded search,
              their own typing, or a safe default. */}
          <p
            className="text-xs text-muted-foreground"
            data-testid="wizard-hours-source-label"
            data-source={hoursSource}
          >
            {hoursSource === 'public_listing'
              ? 'High confidence — public listing found. You can review or edit below.'
              : hoursSource === 'grounded_search'
                ? 'Medium confidence — grounded web search. Please review below.'
                : hoursSource === 'default'
                  ? 'Default suggestion — please review.'
                  : 'You can leave these as defaults; OptiBay won’t block setup if you skip.'}
          </p>
          {hoursEditorOpen && (
          <div className="space-y-2" data-testid="wizard-hours-grid">
            {WEEKDAYS.map((day) => {
              const dayHours = hours[day.key];
              const isOpen = !!dayHours;
              return (
                <div key={day.key} className="flex items-center gap-3">
                  <label className="flex w-32 items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={isOpen}
                      onChange={(e) => toggleDay(day.key, e.target.checked)}
                      data-testid={`wizard-hours-toggle-${day.key}`}
                    />
                    {day.label}
                  </label>
                  {isOpen && (
                    <>
                      <Input
                        type="time"
                        className="w-28"
                        value={dayHours?.open ?? ''}
                        onChange={(e) => updateDay(day.key, 'open', e.target.value)}
                        data-testid={`wizard-hours-open-${day.key}`}
                      />
                      <span className="text-sm text-muted-foreground">to</span>
                      <Input
                        type="time"
                        className="w-28"
                        value={dayHours?.close ?? ''}
                        onChange={(e) => updateDay(day.key, 'close', e.target.value)}
                        data-testid={`wizard-hours-close-${day.key}`}
                      />
                    </>
                  )}
                  {!isOpen && (
                    <span className="text-sm text-muted-foreground">Closed</span>
                  )}
                </div>
              );
            })}
          </div>
          )}
        </div>

        {errors.length > 0 && (
          <ul className="text-sm text-destructive list-disc pl-5" data-testid="wizard-step-errors">
            {errors.map((e, i) => <li key={i}>{e}</li>)}
          </ul>
        )}

        <div className="flex justify-between gap-2">
          {onBack ? <Button variant="outline" onClick={onBack}>Back</Button> : <span />}
          <Button onClick={handleNext} data-testid="wizard-next">Next</Button>
        </div>
      </CardContent>
    </Card>
  );
}
